import { Component, Input, Output, EventEmitter, OnInit, Inject } from 'ng-metadata/core';
import * as loaded from 'image-loaded';

import './image.component.scss';

/**
 * @ngdoc directive
 * @name mwContentDocumentImage
 * @module mw.components.contentDocuments
 *
 * @param {string} [image-url] - The URL of the image
 * @param {string} [display-mode] - The mode will determine the size of the image, 'standard'(default), 'compact'
 * @param {string} [orientation] - The orientation of the image, landscape'(default), 'portrait', 'square'
 * @param {number} [min-image-height] - The minimum image height for the image to be displayed.
 * @param {number} [min-image-width] - The minimum image width for the image to be displayed.
 * @param {expression} [on-click] - Angular expression to evaluate on click of the media image or video. $event is the ng-click event.
 *
 * @description
 * `<mw-content-document-image>` provides a means for displaying the content document image.
 *
 * @usage
 * <hljs hljs-language="html">
 *
 *   <!-- Display an image using 'imageUrl' input binding -->
 *   <mw-content-document-image image-url="http://somedomain/image.png"></mw-content-document-image>
 *
 * </hljs>
 *
 */
@Component({
    selector: 'mw-content-document-image',
    template: `
        <a  ng-href="{{::$ctrl.linkUrl}}"
            target="_blank"
            ng-click="$ctrl.handleClick($event);"
            data-tracking-event="documentOperation/open"
            class="mw-content-document-image">
            <div ng-if="($ctrl.imageUrl || $ctrl.isPlaceholderVisible) && $ctrl.displayMode === 'compact' && ($ctrl.imageLoaded || $ctrl.isPlaceholderVisible)" style="background-image: url({{$ctrl.imageUrl}})" class="mw-content-document-image--{{::$ctrl.displayMode}} mw-content-document-image--{{::$ctrl.orientation}} mw-content-document-image__wrapper"></div>
            <div ng-if="($ctrl.imageUrl || $ctrl.isPlaceholderVisible) && $ctrl.displayMode !== 'compact' && ($ctrl.imageLoaded || $ctrl.isPlaceholderVisible)" class="mw-content-document-image--{{::$ctrl.displayMode}} mw-content-document-image--{{::$ctrl.orientation}} mw-content-document-image__wrapper">
                <img class="mw-content-document-image__image" ng-src="{{::$ctrl.imageUrl}}" ng-if="$ctrl.imageLoaded"/>
            </div>
            <div class="mw-content-document-image__mask"></div>
        </a>
    `
})
export class ImageComponent implements OnInit {
    @Input('<') imageUrl: string;
    @Input('<') linkUrl: string;
    @Input('<') displayMode: string; // Supported values 'standard'(default), 'compact'
    @Input('<') orientation: string; // Supported values 'landscape'(default), 'portrait', 'square'
    @Input('<') minimumImageHeight: number = 240;
    @Input('<') minimumImageWidth: number = 240;
    @Input('<') isPlaceholderVisible: boolean;


    @Output() onClick = new EventEmitter<ng.IAngularEvent>();

    imageLoaded: boolean = false;

    constructor(
        @Inject('$scope') private $scope,
        @Inject('$timeout') private $timeout,
        @Inject('$element') private $element
    ) {
        'ngInject';
    }

    ngOnInit() {
        this.displayMode = this.displayMode ? this.displayMode : 'standard';
        this.orientation = this.orientation ? this.orientation : 'landscape';

        if (this.imageUrl) {
            this.initImage(this.imageUrl);
        }
    }

    initImage(url: string) {
        const el: HTMLImageElement = document.createElement('img');

        el.src = url;

        loaded(el, (error, preloaded) => {
            this.$timeout( () => {
                if (error) {
                    throw error
                }

                if (this.displayMode === 'compact') {
                    this.imageLoaded = true;
                } else if (el.naturalHeight >= this.minimumImageHeight && el.naturalWidth >= this.minimumImageWidth) {
                    this.imageLoaded = true;
                }

                el.remove();
            });
        })
    }

    handleClick(event: ng.IAngularEvent) {
        this.onClick.emit(event);
    }
}



// WEBPACK FOOTER //
// ./src/lib/components/content-documents/shared/media/image.component.ts